import { useMemo, useState } from 'react';
import { Typography } from '@mui/material';

import { formatIsoDate, isInactiveEpisode } from '../vm/formatters';
import type { HistoryFill, MedicationWorkspaceVM } from '../vm/types';

import { type ActiveFilter, ClinicalCardActions } from './ClinicalCardActions';
import { ClinicalCardFrame } from './ClinicalCardFrame';
import type { ConsentState } from './medicationHistoryConsent';
import { MedicationHistorySection } from './MedicationHistorySection';
import { type MedicationRowActions } from './MedicationRow';
import { MedicationTable } from './MedicationTable';

interface MedicationsCardProps extends MedicationRowActions {
  workspace: MedicationWorkspaceVM;
  /** Which active/inactive filter is selected initially. */
  initialFilter?: ActiveFilter;
  /** Episode ids whose past-prescription drawer starts open. */
  expandedEpisodeIds?: string[];
  /** SureScripts consent state for the pharmacy medication-history section. */
  consentState?: ConsentState;
  /**
   * Pharmacy-claim fills for the history section. Defaults to the design-review
   * examples; containers pass the mapped fills from the unified response.
   */
  historyFills?: HistoryFill[];
  /** SureScripts fills attached to charted medications (rendered in their drawers). */
  attributedFillCount?: number;
  /** Opens the add-medication flow ("+ Add"). */
  onAdd?: () => void;
  /**
   * Records SureScripts consent via the host container. When provided, the
   * history section is fully controlled by `consentState` (no demo simulation).
   */
  onRecordConsent?: () => void;
  /** Notification for the host's analytics — fired after the filter changes. */
  onFilterChanged?: (filter: ActiveFilter) => void;
  /** Org subject language ("patient"/"client") for the consent copy. */
  subject?: string;
}

/**
 * Proposal 0 — AdvancedMD Familiar, shaped to drop into a V1 patient-page card.
 *
 * The card frame (bold "Medications" title + filter + "+ Add") mirrors the
 * shared BaseBlock chrome the live V1 clinical cards use, so patient identity is
 * intentionally omitted — the page header already carries it. One list with a
 * Status column, narrowed by an All / Active / Inactive filter (matching both
 * AdvancedMD and the sibling Diagnoses/Allergies cards) rather than a bespoke
 * tab split. Order/dispense detail hides behind a per-row drawer; the consent-
 * gated pharmacy history sits beneath.
 */
export const MedicationsCard: React.FC<MedicationsCardProps> = ({
  workspace,
  initialFilter = 'all',
  expandedEpisodeIds,
  consentState = 'granted',
  historyFills,
  attributedFillCount,
  onAdd,
  onEdit,
  onManageInDoseSpot,
  onDiscontinue,
  onRemove,
  onHistoryToggled,
  onRecordConsent,
  onFilterChanged,
  subject,
}) => {
  const [filter, setFilter] = useState<ActiveFilter>(initialFilter);

  const handleFilterChange = (value: ActiveFilter) => {
    setFilter(value);
    onFilterChanged?.(value);
  };

  const episodes = useMemo(() => {
    const active = workspace.episodes.filter((episode) => !isInactiveEpisode(episode));
    const inactive = workspace.episodes.filter(isInactiveEpisode);
    if (filter === 'active') return active;
    if (filter === 'inactive') return inactive;
    return [...active, ...inactive]; // all — current regimen first, then past
  }, [workspace.episodes, filter]);

  const emptyLabel =
    filter === 'inactive' ? 'No past medications on file.' : 'No active medications.';

  const reviewed = workspace.lastVerified
    ? `Last reviewed ${formatIsoDate(workspace.lastVerified.date)} · ${workspace.lastVerified.by}`
    : null;

  return (
    <ClinicalCardFrame
      title="Medications"
      actions={
        <ClinicalCardActions
          filter={filter}
          onFilterChange={handleFilterChange}
          filterLabel="Filter medications"
          onAdd={onAdd}
        />
      }
    >
      {reviewed && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pt: 1 }}
        >
          {reviewed}
        </Typography>
      )}
      <MedicationTable
        episodes={episodes}
        expandedEpisodeIds={expandedEpisodeIds}
        emptyLabel={emptyLabel}
        onEdit={onEdit}
        onManageInDoseSpot={onManageInDoseSpot}
        onDiscontinue={onDiscontinue}
        onRemove={onRemove}
        onHistoryToggled={onHistoryToggled}
      />
      <MedicationHistorySection
        subject={subject}
        initialState={consentState}
        state={onRecordConsent ? consentState : undefined}
        fills={historyFills}
        attributedFillCount={attributedFillCount}
        onRecordConsent={onRecordConsent}
      />
    </ClinicalCardFrame>
  );
};
